import React, { memo, useEffect, useState } from 'react'
import Button from '@material-ui/core/Button'
import Checkbox from '@material-ui/core/Checkbox'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import FormControl from '@material-ui/core/FormControl'
import Grid from '@material-ui/core/Grid'
import Input from '@material-ui/core/Input'
import InputLabel from '@material-ui/core/InputLabel'
import ListItemText from '@material-ui/core/ListItemText'
import MenuItem from '@material-ui/core/MenuItem'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { RRule } from 'rrule'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/moment'
import LOG from '../../logging/Logger'

const FREQUENCY = [
  { title: 'Yearly', value: RRule.YEARLY },
  { title: 'Monthly', value: RRule.MONTHLY },
  { title: 'Weekly', value: RRule.WEEKLY },
  { title: 'Daily', value: RRule.DAILY }
]

const WEEKDAYS = [
  { title: 'Monday', value: RRule.MO, position: 0 },
  { title: 'Tuesday', value: RRule.TU, position: 1 },
  { title: 'Wednesday', value: RRule.WE, position: 2 },
  { title: 'Thursday', value: RRule.TH, position: 3 },
  { title: 'Friday', value: RRule.FR, position: 4 },
  { title: 'Saturday', value: RRule.SA, position: 5 },
  { title: 'Sunday', value: RRule.SU, position: 6 }
]

const MONTHS = [
  { title: 'January', value: 1, position: 0 },
  { title: 'February', value: 2, position: 1 },
  { title: 'March', value: 3, position: 2 },
  { title: 'April', value: 4, position: 3 },
  { title: 'May', value: 5, position: 4 },
  { title: 'June', value: 6, position: 5 },
  { title: 'July', value: 7, position: 6 },
  { title: 'August', value: 8, position: 7 },
  { title: 'September', value: 9, position: 8 },
  { title: 'October', value: 10, position: 9 },
  { title: 'November', value: 11, position: 10 },
  { title: 'December', value: 12, position: 11 }
]

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
}

const RepeatDialog = ({ open, onSave, onClose }) => {
  LOG.debug('Render: Repeat Dialog')

  const [frequency, setFrequency] = useState(RRule.MONTHLY)
  const [startDate, setStartDate] = useState(null)
  const [untilDate, setUntilDate] = useState(null)
  const [weekdays, setWeekdays] = useState([])
  const [months, setMonths] = useState([])

  const [rule, setRule] = useState(new RRule({}))

  useEffect(() => {
    const newRule = {}
    if (frequency) {
      newRule.freq = frequency
    }

    newRule.byweekday = weekdays
    newRule.bymonth = months

    LOG.debug(newRule)
    setRule(new RRule(newRule))
  }, [frequency, weekdays, months])

  return (
    <Dialog open={open} onClose={onClose} style={{ minWidth: 400 }}>
      <DialogTitle>Select Repeat Pattern</DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography>{rule.toText()}</Typography>
          </Grid>
          <Grid item xs={12}>
            <FormControl style={{ minWidth: 240 }}>
              <InputLabel shrink>Frequency</InputLabel>
              <Select
                labelId='frequency'
                id='frequency'
                value={frequency}
                onChange={e => setFrequency(e.target.value)}
              >
                {FREQUENCY.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                style={{ minWidth: 240 }}
                margin='normal'
                id='date-picker-dialog'
                format='LL'
                disablePast={true}
                label='Starting'
                value={startDate}
                onChange={date => setStartDate(date)}
                KeyboardButtonProps={{
                  'aria-label': 'change start date'
                }}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                style={{ minWidth: 240 }}
                margin='normal'
                id='date-picker-dialog'
                format='LL'
                disablePast={true}
                label='Until'
                value={untilDate}
                onChange={date => setUntilDate(date)}
                KeyboardButtonProps={{
                  'aria-label': 'change until date'
                }}
              />
            </MuiPickersUtilsProvider>
          </Grid>
          <Grid item xs={12}>
            <TextField
              style={{ minWidth: 240 }}
              id='occurrences'
              label='Occurrences'
              type='number'
              InputLabelProps={{
                shrink: true
              }}
              inputProps={{
                'aria-label': 'occurrences',
                'data-lpignore': 'true',
                min: '0',
                step: '1'
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl style={{ minWidth: 240 }}>
              <InputLabel shrink>Weekday</InputLabel>
              <Select
                labelId='weekday-select'
                id='weekday-select'
                multiple
                value={weekdays}
                onChange={e => setWeekdays(e.target.value)}
                input={
                  <Input
                    inputProps={{
                      'aria-label': 'weekday-select',
                      'data-lpignore': 'true'
                    }}
                  />
                }
                renderValue={selected =>
                  selected.sort
                    .map(
                      weekdayRule =>
                        WEEKDAYS.find(weekday => weekday.value === weekdayRule)
                          .title
                    )
                    .join(', ')
                }
                MenuProps={MenuProps}
              >
                {WEEKDAYS.map(weekday => (
                  <MenuItem key={weekday.value} value={weekday.value}>
                    <Checkbox checked={weekdays.indexOf(weekday.value) > -1} />
                    <ListItemText primary={weekday.title} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl style={{ minWidth: 240 }}>
              <InputLabel shrink>Month</InputLabel>
              <Select
                labelId='month-select'
                id='month-select'
                multiple
                value={months}
                onChange={e => setMonths(e.target.value)}
                input={
                  <Input
                    inputProps={{
                      'aria-label': 'month-select',
                      'data-lpignore': 'true'
                    }}
                  />
                }
                renderValue={selected =>
                  selected
                    .map(
                      monthRule =>
                        MONTHS.find(month => month.value === monthRule).title
                    )
                    .join(', ')
                }
                MenuProps={MenuProps}
              >
                {MONTHS.map(month => (
                  <MenuItem key={month.value} value={month.value}>
                    <Checkbox checked={months.indexOf(month.value) > -1} />
                    <ListItemText primary={month.title} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color='primary'>
          Cancel
        </Button>
        <Button onClick={onClose} color='primary'>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(RepeatDialog)
